import MacheteController from '../controller';
import { getModel } from './model';
import { findAndGroupChildrenByType } from './segmentationTree';
import DEFAULTS from '../constants/defaults';
import NODE_TYPES from '../types';
import { convertSegmentTo3DLine, convertPointTo3DVector } from './vector';
import { DOOR_LABEL_IDS, VENT_LABEL_ID } from '../constants/labels';

let segmentsOutlineEntities = [];
let textHelpers = [];

const isDoor = (element) =>
  element.labels.some((labelId) => DOOR_LABEL_IDS.has(labelId));
const isVent = (element) => element.containsLabel(VENT_LABEL_ID);

/**
 * Takes an element uuid and finds the element from the model
 * @param {string} uuid - a machete element uuid
 * @returns {Hover.ModelElement} the actual element
 * @see http://engineering.hover.to/#Hover.ModelElement
 */
export const getElement = (uuid) => {
  const { elements } = getModel();
  for (let i = 0; i < elements.length; i += 1) {
    if (elements[i].uuid === uuid) {
      return elements[i];
    }
  }
  return undefined;
};

/**
 * Returns a map of the elements on the model
 * @returns {Object} a map of the elements where the key is the uuid and the value is the element id with element type
 * @see http://engineering.hover.to/#Hover.ModelElement
 */
const getElementMap = () =>
  getModel().elements.reduce((acc, el) => {
    acc[el.uuid] = {
      uuid: el.uuid,
      type: el instanceof Hover.Facade ? 'facade' : 'annotation',
    };
    return acc;
  }, {});

/**
 * @param {string} nodeRef
 * Returns an array of all the visible modelElements on a node
 * @returns {array} of all visible elements
 * @see http://engineering.hover.to/#Hover.ModelElement
 */
export const findAllVisibleElementsForNode = (nodeRef) =>
  Object.entries(
    findAndGroupChildrenByType(nodeRef, NODE_TYPES.OPENING_PACKAGE_TYPES, {
      deep: true,
    }),
  ).reduce(
    (acc, [type, node]) =>
      acc.concat(
        node
          .filter((n) => n.modelElements.length > 0)
          .map((n) => ({ type, element: n.modelElements[0] })),
      ),
    [],
  );

export const elementHasText = (element) =>
  element && element.metadata && element.metadata.text;

export const removeTextFromElement = (element) => {
  if (!elementHasText(element)) return;
  const { helperEntities } = getModel();
  const { text } = element.metadata;
  helperEntities.remove(text);
  helperEntities.setNeedsUpdate(true);
  delete element.metadata.text; // eslint-disable-line
};

export const addOutlineToElement = (element, opts = DEFAULTS.outlineOpts()) => {
  element.includeOutline = true; // eslint-disable-line
  element.outline.configure(opts);
};

export const removeOutlineFromElement = (element) => {
  if (element && element.includeOutline) element.includeOutline = false; // eslint-disable-line
};

export const setDefaultVisibility = (opts = DEFAULTS.visibilityOpts()) => {
  const { helperEntities, elements } = getModel();
  elements.forEach((element) => {
    element.useColoring = opts.coloring; // eslint-disable-line
    removeTextFromElement(element);
    if (element instanceof Hover.Facade) {
      element.visible = opts.visibleAll; // eslint-disable-line
    }
    if (element instanceof Hover.Annotation) {
      if (isDoor(element) || isVent(element)) {
        removeOutlineFromElement(element);
        element.visible = opts.visibleDoorVent; // eslint-disable-line
      } else {
        element.visible = opts.visibleAll; // eslint-disable-line
      }
    }
  });

  segmentsOutlineEntities.forEach((entity) => helperEntities.remove(entity));
  segmentsOutlineEntities = [];
  textHelpers.forEach((textHelper) => helperEntities.remove(textHelper));
  textHelpers = [];
};

export const addTextToAnnotation = (annotation) => {
  if (elementHasText(annotation)) return;

  const textHelper = new Hover.Machete.TextHelper();
  textHelper.configure({
    ...DEFAULTS.annotationTextOpts(),
    direction: annotation.normal,
    centroid: annotation.centroid,
    faceText: annotation.name,
  });

  textHelper.create(annotation);
  getModel().helperEntities.add(textHelper);
  textHelpers.push(textHelper);

  annotation.metadata.text = textHelper; // eslint-disable-line

  textHelper.setNeedsUpdate(true);
};

export const addTextToFacade = (facade) => {
  if (elementHasText(facade)) return;
  const textElements = new Hover.Machete.TextHelper(facade, {
    ...DEFAULTS.facadeTextOpts(),
    direction: facade.normal,
    centroid: facade.centroid,
  });

  getModel().helperEntities.add(textElements);
  facade.metadata.text = textElements; // eslint-disable-line

  textElements.setNeedsUpdate(true);
};

export const addEdgesLengths = (edgesArray) => {
  const facadesMap = getModel().facades.reduce((acc, facade) => {
    acc[facade.id] = facade;
    return acc;
  }, {});
  let textHelper;

  edgesArray.forEach(({ facades: facadeIds, start, end }) => {
    const facades = facadeIds.map((facadeId) => facadesMap[facadeId]);
    const vectorPair = [
      convertPointTo3DVector(start.position),
      convertPointTo3DVector(end.position),
    ];

    facades.forEach((facade) => {
      if (facade === undefined) return; // split feature in Design Pro causes facades to sometimes be undefined
      textHelper = new Hover.Machete.TextHelper();
      textHelper.configure({
        ...DEFAULTS.textLengthsOpts(),
        geometricElementReference: facade,
        direction: facade.normal,
        centroid: facade.centroid,
      });

      textHelper.create([vectorPair]);
      getModel().helperEntities.add(textHelper);
      textHelpers.push(textHelper);

      textHelper.setNeedsUpdate(true);
    });
  });
};

export const addOutlineToEdges = (
  edgesArray,
  opts = DEFAULTS.segmentsOutlineOpts(),
) => {
  const edgeHelper = new Hover.Machete.EdgeHelper(
    edgesArray.map(convertSegmentTo3DLine),
  );
  edgeHelper.configure(opts);
  getModel().helperEntities.add(edgeHelper.entity);
  segmentsOutlineEntities.push(edgeHelper.entity);
  edgeHelper.entity.setNeedsUpdate(true);
  addEdgesLengths(edgesArray);
};
// TODO: This is a placeholder for testing refactor; Remove for 1.0
export const addOutlineToSegments = addOutlineToEdges;

export const setOutlineColor = (color) => {
  MacheteController.instance.setOutlineColor(color);
};

export const onlyOutlineAllFacades = (opts = DEFAULTS.outlineOpts()) => {
  const { facades, annotations } = getModel();
  facades.forEach((facade) => addOutlineToElement(facade, opts));
  annotations.forEach((annotation) => removeOutlineFromElement(annotation));
};

export const onlyShowSelectedElements = (uuids = []) => {
  const uuidsSet = new Set(uuids);
  getModel().elements.forEach((element) => {
    if (uuidsSet.has(element.uuid)) {
      element.visible = true; // eslint-disable-line
    } else {
      element.visible = false; // eslint-disable-line
    }
  });
};

export default {
  getElement,
  getElementMap,
  findAllVisibleElementsForNode,
  removeTextFromElement,
  elementHasText,
  addOutlineToElement,
  removeOutlineFromElement,
  setOutlineColor,
  onlyOutlineAllFacades,
  setDefaultVisibility,
  addTextToAnnotation,
  addTextToFacade,
  addEdgesLengths,
  addOutlineToEdges,
  onlyShowSelectedElements,
  addOutlineToSegments,
};
